import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth.jsx'
import { useCredits } from '../hooks/useCredits'
import { useSubscription } from '../hooks/useSubscription'
import { Card, CardHeader, CardTitle, CardDescription, Button, AnimatedLine } from '../components/UI'
import { SubscriptionCard, CreditBalance, UsageChart, CreditPacks } from '../components/Dashboard'
import { User, CreditCard, BarChart3, Settings, Zap, ExternalLink, ChevronRight } from 'lucide-react'
import { openCustomerPortal } from '../services/stripe'

const DashboardView = () => {
  const { user } = useAuth()
  const { credits, history, loading: creditsLoading } = useCredits()
  const { subscription, loading: subLoading, isPro, isSubscribed } = useSubscription()
  const [showCreditPacks, setShowCreditPacks] = useState(false)

  const loading = creditsLoading || subLoading

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">
            Welcome back, <span className="gradient-text">{user?.displayName || user?.email?.split('@')[0] || 'there'}</span>
          </h1>
          <p className="text-dark-400">
            Manage your subscription and credits
          </p>
          <div className="mt-4 w-24">
            <AnimatedLine trigger={true} color="accent" />
          </div>
        </div>

        {/* Low Credits Alert */}
        {credits && credits.balance > 0 && credits.allottedMonthly > 0 && credits.balance < (credits.allottedMonthly * 0.2) && (
          <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <CreditCard className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="font-medium text-yellow-300">Running low on credits</p>
                <p className="text-sm text-yellow-400/70">You have {credits.balance.toLocaleString()} credits remaining</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowCreditPacks(true)}>
              Buy Credits
            </Button>
          </div>
        )}

        {/* Main Stats Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Credit Balance */}
          <CreditBalance 
            credits={credits} 
            loading={creditsLoading}
            onBuyCredits={() => setShowCreditPacks(true)}
          />

          {/* Subscription Card */}
          <SubscriptionCard 
            subscription={subscription} 
            loading={subLoading} 
          />

          {/* Account Card */}
          <Card>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-dark-400 text-sm">Account</p>
                <p className="text-lg font-medium mt-1 truncate max-w-[180px]">
                  {user?.email}
                </p>
              </div>
              <div className="p-2 bg-accent-500/10 rounded-lg">
                <User className="w-5 h-5 text-accent-400" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-dark-700/50 space-y-2">
              <p className="text-sm text-dark-500">
                {user?.emailVerified ? '✓ Email verified' : 'Email not verified'}
              </p>
              <Link to="/settings">
                <Button variant="ghost" size="sm">
                  <Settings className="w-4 h-4" />
                  Account Settings
                </Button>
              </Link>
            </div>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Usage History Chart */}
          <UsageChart history={history} loading={creditsLoading} />

          {/* Quick Actions / Credit Packs */}
          {showCreditPacks ? (
            <CreditPacks onClose={() => setShowCreditPacks(false)} />
          ) : (
            <Card padding="lg">
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Common tasks and shortcuts</CardDescription>
              </CardHeader>

              <div className="space-y-3">
                <button 
                  onClick={() => setShowCreditPacks(true)}
                  className="w-full p-4 bg-dark-800 hover:bg-dark-700 rounded-xl transition-colors text-left flex items-center gap-3 group"
                >
                  <div className="p-2 bg-accent-500/10 rounded-lg group-hover:bg-accent-500/20 transition-colors">
                    <CreditCard className="w-5 h-5 text-accent-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">Buy Credits</p>
                    <p className="text-sm text-dark-400">Top up your credit balance</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-dark-500 group-hover:text-dark-400 transition-colors" />
                </button>

                <Link 
                  to="/pricing"
                  className="w-full p-4 bg-dark-800 hover:bg-dark-700 rounded-xl transition-colors text-left flex items-center gap-3 group block"
                >
                  <div className="p-2 bg-accent-500/10 rounded-lg group-hover:bg-accent-500/20 transition-colors">
                    <BarChart3 className="w-5 h-5 text-accent-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">View Plans</p>
                    <p className="text-sm text-dark-400">Compare subscription options</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-dark-500 group-hover:text-dark-400 transition-colors" />
                </Link>

                {isSubscribed && (
                  <button 
                    onClick={() => openCustomerPortal()}
                    className="w-full p-4 bg-dark-800 hover:bg-dark-700 rounded-xl transition-colors text-left flex items-center gap-3 group"
                  >
                    <div className="p-2 bg-accent-500/10 rounded-lg group-hover:bg-accent-500/20 transition-colors">
                      <ExternalLink className="w-5 h-5 text-accent-400" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">Billing Portal</p>
                      <p className="text-sm text-dark-400">Manage payment methods & invoices</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-dark-500 group-hover:text-dark-400 transition-colors" />
                  </button>
                )}

                <Link 
                  to="/settings"
                  className="w-full p-4 bg-dark-800 hover:bg-dark-700 rounded-xl transition-colors text-left flex items-center gap-3 group block"
                >
                  <div className="p-2 bg-accent-500/10 rounded-lg group-hover:bg-accent-500/20 transition-colors">
                    <Settings className="w-5 h-5 text-accent-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">Settings</p>
                    <p className="text-sm text-dark-400">Account preferences & security</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-dark-500 group-hover:text-dark-400 transition-colors" />
                </Link>
              </div>
            </Card>
          )}
        </div>

        {/* Upgrade Banner (for non-Pro users) */}
        {!isPro && (
          <Card className="mt-8 bg-gradient-to-r from-accent-900/20 to-accent-800/20 border-accent-500/20">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-accent-500/20 rounded-xl hidden sm:block">
                  <Zap className="w-6 h-6 text-accent-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">
                    {isSubscribed ? 'Upgrade to Pro' : 'Get Started with Pro'}
                  </h3>
                  <p className="text-dark-400">
                    Get 2,000 credits/month, priority support, and advanced features
                  </p>
                </div>
              </div>
              <Link to="/pricing">
                <Button className="whitespace-nowrap">
                  <Zap className="w-4 h-4" />
                  {isSubscribed ? 'Upgrade Now' : 'View Plans'}
                </Button>
              </Link>
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}

export default DashboardView
